import { supabase } from '../lib/supabase';
import { getDifficultyConfig } from './lumoPersonalityService';
import adaptiveLearningService from './adaptiveLearningService';

// Apply tenant filter when tenantId is provided
const applyTenant = (query, tenantId) => (tenantId ? query?.eq('tenant_id', tenantId) : query);

// Curated universal beginner drills as a safe fallback when DB selection is empty
// Based on Mega Trainer curriculum - universal for any sales role/niche
const CURATED_BEGINNER_DRILLS = [
  // === LUMO CORE PRINCIPLES ===
  {
    id: 'curated_beg_001',
    industry: 'Global',
    type: 'training',
    title: 'Even-Tone Opening: Rapport + Purpose',
    prompt_text:
      "You're speaking with a new prospect. Open the conversation using even-tone authenticity: establish quick rapport (one line), then clearly state the purpose and agenda for the call in a confident, buyer-friendly way. Keep it conversational and pressure-free.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'lumo', 'opening'],
    hints: [
      "Start with a genuine smile in your voice—it naturally slows your speech and makes you sound more trustworthy. People can hear emotion through tone.",
      "Mention a specific reason why you're calling (reference, mutual connection, research) within the first 10 seconds to justify the interruption.",
      "Use the permission close immediately: 'Do you have 30 seconds?' This gets an early 'yes' and respects their time boundary.",
      "Avoid rushed speech, filler words, or apologetic language. Pause before speaking to collect your thoughts and project natural confidence.",
      "State your purpose clearly in one sentence: what you're discussing, why it matters to them, and what the call outcome will be."
    ]
  },
  {
    id: 'curated_beg_002',
    industry: 'Global',
    type: 'training',
    title: 'Micro-Probes: High-Value Discovery Questions',
    prompt_text:
      "Ask 3-5 micro-probe discovery questions that uncover the prospect's current situation, challenges, and desired outcomes. Keep questions short, non-threatening, and universal—no industry jargon. Let the prospect guide the conversation.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'lumo', 'discovery'],
    hints: [
      "Start with broadopen-ended questions about their role and responsibilities. 'What does your day-to-day look like?' reveals context without assumptions.",
      "Ask about process and current tools: 'How do you currently approach X?' or 'What's your process for Y?' These uncover both methods and pain points.",
      "Use silence as a tool—when they finish, pause for 2-3 seconds before responding. They often fill silence with deeper insights.",
      "Stay curious and non-judgmental. Avoid leading questions that hint at your solution. Your job is discovery, not influence.",
      "Connect their answers: 'Earlier you mentioned X, and now you're saying Y. Help me understand how those two relate.' Shows listening and builds trust."
    ]
  },
  {
    id: 'curated_beg_003',
    industry: 'Global',
    type: 'training',
    title: 'Neutral Confidence: Handle "Send Me Info"',
    prompt_text:
      "Prospect says: 'Can you just send me some info?' Use neutral confidence to acknowledge, clarify intent, and re-position for the next step without pressure. Practice Lumo's even-tone approach—no manipulation, just clarity.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'lumo', 'objection'],
    hints: [
      "Never say 'no' directly. Instead: 'I absolutely can send something. Before I do, quick question—what specifically would be most valuable for you?'",
      "Clarify intent first: Are they genuinely interested, or delaying? Ask: 'Are you interested in exploring this, or is this more informational right now?'",
      "Reposition: 'Let me ask—if I sent info, would you look at it this week? And if it looked good, would we discuss by next Friday?'",
      "Make a gentle ask: 'I can send the overview, but it's custom-built once I understand your full situation. Can we jump on a quick call Friday?' Gets the actual conversation.",
      "Use assumptive language: 'Perfect. After you review it, let's circle back Wednesday to discuss what makes sense.' Presumes forward movement."
    ]
  },
  {
    id: 'curated_beg_004',
    industry: 'Global',
    type: 'training',
    title: 'Continuous Improvement: Self-Debrief Practice',
    prompt_text:
      "After a mock call, practice the Lumo improvement loop: identify one thing you did well, one area to improve, and state your specific action for the next call. Keep it simple and actionable.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'lumo', 'reflection'],
    hints: [
      "Start with wins: 'I did well at X because...' Reinforces good habits. Be specific: 'I asked open-ended questions' not 'I was good at discovery.'",
      "Identify gaps honestly: 'I rushed through discovery' or 'I didn't listen for emotional cues.' Honest self-assessment accelerates growth.",
      "State a concrete action: 'Next call, I'll pause for 3 seconds after they answer.' Specificity makes improvement measurable, not vague.",
      "Avoid judgment: 'I was terrible at objection handling' sounds bad. Instead: 'My opportunity is learning to handle price objections better.'",
      "Track patterns: After 5 calls, you'll see recurring themes. That's where your focus should be. Master one skill at a time."
    ]
  },

  // === SPIN SELLING BASICS ===
  {
    id: 'curated_beg_005',
    industry: 'Global',
    type: 'training',
    title: 'SPIN: Situation Questions',
    prompt_text:
      "Ask 2-3 situation questions to understand the prospect's current state. Focus on facts: their current process, team structure, tools they use. Keep questions open-ended and non-intrusive.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'spin', 'discovery'],
    hints: [
      "Situation questions are fact-finding, not leading. Ask 'What tools do you currently use?' not 'Are you happy with your current tools?' Stay neutral.",
      "Start with open-ended questions: 'How does your team currently...?' 'What's your process for...?' These reveal information and build trust.",
      "Ask one question at a time and pause for their answer. Resist the urge to fill silence—let them expand on their response naturally.",
      "Look for background context: company size, team structure, current challenges, existing solutions, decision-making process, budget cycles.",
      "Take mental notes of their language and pain points. This helps you ask better Problem and Implication questions next."
    ]
  },
  {
    id: 'curated_beg_006',
    industry: 'Global',
    type: 'training',
    title: 'SPIN: Problem Questions',
    prompt_text:
      "After understanding the situation, ask 2-3 problem questions that uncover difficulties or dissatisfaction with the current state. Keep tone curious, not prescriptive.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'spin', 'discovery'],
    hints: [
      "Connect problem questions to the Situation revealed. Reference their answer: 'You mentioned using spreadsheets. How is that affecting your team efficiency?'",
      "Listen for emotion—real problems have feeling behind them. That emotion signals genuine pain worth solving.",
      "Ask about inefficiencies, wasted time, missed opportunities, or team friction. 'What's the impact of this process on your goals?'",
      "Avoid leading questions. Let them define what's a problem to them, not what you think should be a problem.",
      "Build a mental list: each problem mentioned is a potential value lever later. More problems surfaced = stronger your solution case."
    ]
  },
  {
    id: 'curated_beg_007',
    industry: 'Global',
    type: 'training',
    title: 'SPIN: Implication Questions (Simple)',
    prompt_text:
      "Based on a problem the prospect shared, ask one implication question that explores the consequences of not solving it. Example: 'What happens if this continues for another quarter?' Keep it gentle.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'spin', 'discovery'],
    hints: [
      "Implication questions connect the problem to bigger consequences: 'If this continues, what happens to X?' or 'How does this affect your ability to Y?'",
      "Think in chains: problem → impact → consequence. If they struggle with process X, that means Y, which means Z. Help them see the full chain.",
      "Build business case components: cost of the problem, lost revenue, team morale, customer impact, competitive risk. Make the problem feel real.",
      "Don't suggest the answer. Your job is to ask, not to lead. Let them come to their own conclusion that the problem is worth solving.",
      "Listen for 'need'—when they say 'We really need to fix this' or 'This is becoming critical,' you've built value for your solution."
    ]
  },

  // === SANDLER BASICS ===
  {
    id: 'curated_beg_008',
    industry: 'Global',
    type: 'training',
    title: 'Sandler: Bonding & Rapport',
    prompt_text:
      "Practice Sandler's bonding phase: open with genuine curiosity, find common ground, and create a comfortable environment before business talk. Keep it natural, not scripted.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'sandler', 'opening'],
    hints: [
      "Start with genuine human interest before business: 'How's your day going?' or 'Did you have a busy week?' Authentic curiosity builds rapport faster than small talk scripts.",
      "Find common ground: shared industry experience, mutual connections, similar challenges. 'I worked with a lot of teams in your space—there's a common thread here.'",
      "Be natural, not scripted. Avoid canned small talk about weather or sports. Let the conversation flow based on what they mention.",
      "Mirror their energy and pace: If they're warm and talkative, engage. If they're brief, respect that. Adapt without mimicking.",
      "Move into business talk naturally: 'I appreciate that context. Let me tell you why I called today...' Bonding creates permission for business conversation."
    ]
  },
  {
    id: 'curated_beg_009',
    industry: 'Global',
    type: 'training',
    title: 'Sandler: Up-Front Contract',
    prompt_text:
      "Set an up-front contract for the call: clarify the purpose, what you'll cover, and agree on next steps at the end. Practice Sandler's permission-based approach.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'sandler', 'opening'],
    hints: [
      "Start with permission: 'I know you're busy. I have something that might be relevant. Can I spend a few minutes sharing what I'm thinking?'",
      "Set the contract clearly: 'Here's what I'd love to cover: your current situation, what's working, what's not, and if it makes sense, next steps.'",
      "Add a time boundary: 'I'm thinking 20 minutes tops. Does that work?' Respects their time and shows professionalism.",
      "Get agreement: 'If we find something worth exploring, would you be open to a next step? If not, we'll know it's not the right fit.'",
      "Check in mid-call: 'I know we're covering a lot—are you still comfortable, or should we shift direction?' Shows you're paying attention to them."
    ]
  },
  {
    id: 'curated_beg_010',
    industry: 'Global',
    type: 'training',
    title: 'Sandler: Pain Discovery',
    prompt_text:
      "Ask pain discovery questions to uncover the emotional and business impact of the prospect's challenges. Use Sandler's gentle, permission-based style: 'Can I ask about...?'",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'sandler', 'discovery'],
    hints: [
      "Use permission-based language: 'Can I ask you something about how you're handling X?' gives them control and builds trust.",
      "Dig deeper with follow-ups: After they answer, ask 'Is that your biggest challenge, or is there something else?' Uncovers hidden pain.",
      "Listen for emotion—Sandler emphasizes emotional discovery. If they sound frustrated or resigned, acknowledge it: 'That sounds tough.'",
      "Ask about business impact: 'How is that affecting your ability to hit your revenue goals?' or 'What's the team cost of this issue?'",
      "Get them to quantify pain: 'How much time do you lose each week?' or 'What's your estimate of the cost?' Numbers make pain real."
    ]
  },

  // === CHALLENGER BASICS ===
  {
    id: 'curated_beg_011',
    industry: 'Global',
    type: 'training',
    title: 'Challenger: Insight Teaching (Simple)',
    prompt_text:
      "Share a universal insight that challenges how the prospect thinks about their problem. Example: 'Most teams think the issue is X, but data shows it's really Y.' Keep it educational, not salesy.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'challenger', 'insight'],
    hints: [
      "Lead with data or evidence: 'We've seen in companies like yours that...' backs up your insight with credibility, not opinion.",
      "Challenge their assumption gently: 'Most assume X is the problem, but we've found...' uses common experience to introduce new thinking.",
      "Make it relevant: Connect the insight directly to their situation. 'Based on what you shared about your process, I'm thinking...'",
      "Ask for their reaction: 'Does that resonate with what you're seeing?' Gets them engaged and thinking, not just listening.",
      "Avoid being preachy: Share insight as perspective, not the final answer. Invite them to test the idea: 'Want to explore that?'"
    ]
  },

  // === NEPQ BASICS ===
  {
    id: 'curated_beg_012',
    industry: 'Global',
    type: 'training',
    title: 'NEPQ: Connection Phase',
    prompt_text:
      "Start with NEPQ's connection phase: build trust before diving into business. Use open-ended questions that show genuine interest in the person, not just the deal.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'nepq', 'opening'],
    hints: [
      "Start with the person, not the problem: 'Tell me a bit about your role—how did you get into this space?' builds human connection.",
      "Listen for values and priorities: What matters to them? Career growth? Team success? Company mission? Understanding values builds rapport.",
      "Find common ground: 'I see you're from the same region I am—what's your experience been there?' Shared experience builds trust.",
      "Acknowledge their journey: 'It sounds like you've built a really strong process over time.' Recognition validates and softens the conversation.",
      "Transition naturally: 'Given what you've shared about your goals, I'm curious about how you're currently handling...' Links personal context to business."
    ]
  },
  {
    id: 'curated_beg_013',
    industry: 'Global',
    type: 'training',
    title: 'NEPQ: Problem Awareness',
    prompt_text:
      "Use NEPQ problem awareness questions to help the prospect articulate their own problem. Example: 'What's been the biggest challenge with [area]?' Let them own the problem.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'nepq', 'discovery'],
    hints: [
      "Ask open-ended problem questions: 'What's been the biggest challenge with your current approach?' or 'Where do you see the most friction?'",
      "Let them talk freely: Don't interrupt or try to fix it yet. Let them vent, explain, and fully own the problem.",
      "Probe the emotional side: 'How is that affecting you and your team?' helps them articulate not just the problem but its impact.",
      "Reflect back: 'So the core issue is X, which is causing Y—is that right?' Shows you're tracking and helps them clarify their own thinking.",
      "Build on their words: When they articulate the problem, use their language back. 'You mentioned X being critical—talk me through that.' Validates their thinking."
    ]
  },

  // === GAP SELLING BASICS ===
  {
    id: 'curated_beg_014',
    industry: 'Global',
    type: 'training',
    title: 'GAP Selling: Current State Discovery',
    prompt_text:
      "Discover the prospect's current state: where they are today, what's working, what's not. Ask quantifiable questions: 'How many...?', 'How long...?', 'What percentage...?'",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'gap_selling', 'discovery'],
    hints: [
      "Start with 'How are you currently handling X?' This establishes the baseline—what exists today and how well it works.",
      "Ask for quantifiable data: 'How many hours per week...?' 'What's your current conversion rate?' Numbers make the problem real and measurable.",
      "Explore what works well: 'What's going right with your current approach?' Understand their wins—these become leverage later.",
      "Identify specific gaps: 'Where do you feel like there's room for improvement?' Listen for hesitation or frustration—that signals true gaps.",
      "Map the full picture: Current tools, team size, process steps, timelines, costs. The more comprehensive, the bigger the gap you can later show."
    ]
  },
  {
    id: 'curated_beg_015',
    industry: 'Global',
    type: 'training',
    title: 'GAP Selling: Future State Exploration',
    prompt_text:
      "Help the prospect define their desired future state: 'If we solve this, what does success look like in 6 months?' Get them to paint the vision.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'gap_selling', 'discovery'],
    hints: [
      "Ask them to paint a picture: 'Imagine we solve this in 6 months—what's different?' Get vivid, specific language about the future.",
      "Connect to their goals: 'How would that help you hit your revenue targets?' or 'What would that mean for your team?' Makes it personal.",
      "Get quantifiable outcomes: 'How much time would you save?' 'What's the revenue impact?' Numbers build the business case.",
      "Explore the ripple effects: 'If your team is more efficient, what else becomes possible?' Helps them see the full value chain.",
      "Get them to own it: 'Does that future state feel realistic and valuable to you?' They articulate the vision themselves, making it theirs."
    ]
  },

  // === STRAIGHT LINE BASICS ===
  {
    id: 'curated_beg_016',
    industry: 'Global',
    type: 'training',
    title: 'Straight Line: Tonality Practice',
    prompt_text:
      "Practice tonality control: say the same sentence three ways—excited, confident, and curious. Notice how tone changes meaning. Master even-tone confidence for trust-building.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'straight_line', 'tonality'],
    hints: [
      "Try the same phrase three ways: excited (high energy), confident (direct and assured), curious (questioning and learning). Notice how meaning shifts.",
      "Record yourself: Hear how you sound. Are you rushing? Flat? Too aggressive? Self-awareness is the first step to mastering tonality.",
      "Practice even-tone confidence: Not excited, not flat—calm, assured, credible. This is the most trustworthy tone for discovery and objection handling.",
      "Match their energy: If they're energetic, dial up slightly. If they're cautious, dial down. Matching without mimicking builds rapport.",
      "Notice your patterns: Do you rush when nervous? Go flat when pitching? Raise your pitch at the end of sentences? Identify patterns to correct them."
    ]
  },
  {
    id: 'curated_beg_017',
    industry: 'Global',
    type: 'training',
    title: 'Straight Line: Build Certainty',
    prompt_text:
      "Practice building certainty in the prospect: certainty in you (trust), certainty in the solution (value), certainty in the company (credibility). Use stories and social proof.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'straight_line', 'certainty'],
    hints: [
      "Build certainty in you: Share a relevant success story or qualification that proves you know your stuff. 'We've worked with 50+ companies in your space and seen X.'",
      "Build certainty in the solution: Use case studies, metrics, results. 'Teams typically see a 40% time savings within 3 months.' Numbers build confidence.",
      "Build certainty in the company: Share company milestones, funding, awards, or customer names (when allowed). Establishes stability and credibility.",
      "Tell micro-stories: 'One client told me they lost $50K before fixing this. After implementation...' Stories stick and build emotional certainty.",
      "Sequence them: Build certainty in you first (trust), then the solution (value), then the company (stability). Trust + value + credibility = willingness to move forward."
    ]
  },

  // === VALUE SELLING BASICS ===
  {
    id: 'curated_beg_018',
    industry: 'Global',
    type: 'training',
    title: 'Value Selling: Value Hypothesis',
    prompt_text:
      "Create a simple value hypothesis: 'Based on what you shared, solving [problem] could mean [quantified outcome]. Does that resonate?' Frame value in business terms, not features.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'value_selling', 'value'],
    hints: [
      "Build your hypothesis on their words: 'You mentioned losing 20 hours a week. If we reclaimed that, what's that worth to your team?'",
      "Use quantified outcomes: Not 'We'll make you more efficient' but 'You'll recover 20 hours per week, which equals $X in productivity.'",
      "Frame in business language they care about: Revenue, cost savings, team capacity, customer satisfaction, risk reduction. Not features.",
      "Test it with them: 'Does that resonate?' gets their reaction and invites them into the hypothesis. If it doesn't land, probe more.",
      "Keep it simple: One problem, one quantified outcome. Don't overwhelm with multiple value statements. Mastery is clarity."
    ]
  },
  {
    id: 'curated_beg_019',
    industry: 'Global',
    type: 'training',
    title: 'Value Selling: Quantify Outcomes',
    prompt_text:
      "Help the prospect quantify the value of solving their problem. Ask: 'If we improve X by Y%, what's that worth to you?' Get them to do the math.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'value_selling', 'value'],
    hints: [
      "Start with their current state numbers: 'You said you spend 20 hours per week on X. What's your hourly cost per person?' Builds the baseline.",
      "Calculate together: 'So 20 hours × 5 people × $50/hour = $5K per week. If we cut that in half, what's that worth annually?' Make them do the math.",
      "Use their metrics: Revenue, cost, time, customer satisfaction. Whatever matters to them, quantify improvement in those terms.",
      "Ask the 'worth' question: 'What would reclaiming $250K annually in productivity be worth to your team?' Gets them to articulate value.",
      "Connect to their goal: 'If we improved efficiency, would that help you hit your revenue targets?' Links quantified value to their priorities."
    ]
  },

  // === OBJECTION HANDLING BASICS ===
  {
    id: 'curated_beg_020',
    industry: 'Global',
    type: 'training',
    title: 'Objection: Price Pushback (Early)',
    prompt_text:
      "Prospect asks early: 'What does it cost?' Give a universal, buyer-first response that frames value, shares ranges responsibly, and transitions back to discovery.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'objection', 'price'],
    hints: [
      "Never answer price directly without context. Use a bridge: 'Great question. Price varies based on your situation. Can I ask a few quick things first?'",
      "Ask about their budget first: 'What range are you comfortable working with?' Understand their constraints before quoting.",
      "Share ranges, not exact prices: 'Depending on company size, we typically see deployments in the $X-$Y range.' Gives context without locking in.",
      "Connect price to value: 'For a company your size, we typically recover the investment within 6 months through X.' Frames cost against value.",
      "Transition back to discovery: 'Once I understand your full situation, I can give you a more accurate range. Let me ask...'"
    ]
  },
  {
    id: 'curated_beg_021',
    industry: 'Global',
    type: 'training',
    title: 'Objection: "I Need to Think About It"',
    prompt_text:
      "Prospect says: 'I need to think about it.' Use acknowledge-probe-redirect: acknowledge their need, probe what specifically they're thinking about, redirect to clarity.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'objection', 'stalling'],
    hints: [
      "Never dismiss their need to think. Acknowledge it: 'That makes total sense. Taking time to think is smart.'",
      "Probe the real concern: 'What specifically are you thinking about?' Often it's not thinking—it's doubt, budget, or timeline concerns.",
      "Listen for the hidden objection: If they say 'thinking,' ask follow-ups: 'Is it the budget? The timeline? Or something else?' Surface the real issue.",
      "Redirect to clarity: 'What information would help you make a decision faster?' or 'What would make you comfortable moving forward?'",
      "Set a follow-up timeline: 'Great. Let's reconnect Thursday. By then, what would you like to have explored?' Makes 'thinking' actionable."
    ]
  },
  {
    id: 'curated_beg_022',
    industry: 'Global',
    type: 'training',
    title: 'Objection: Competitor Mention',
    prompt_text:
      "Prospect mentions a competitor. Don't trash-talk. Instead: 'They're a solid choice. What's important to you in making this decision?' Redirect to criteria.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'objection', 'competition'],
    hints: [
      "Never trash-talk competitors. It makes you look defensive and unprofessional. 'They're a solid company. What appeals to you about them?'",
      "Redirect to criteria: 'Every company has strengths. What's most important to you in a solution?' Puts focus back on their needs, not competitors.",
      "Understand their comparison: 'What are the main differences you're weighing?' Knowing what matters helps you position more effectively.",
      "Focus on fit: 'For companies in your situation, we typically focus on X. Does that align with your priorities?' Positions your strengths.",
      "Get commitment to next steps: 'What would it take for you to move forward with us?' Shifts from comparison to decision."
    ]
  },
  {
    id: 'curated_beg_023',
    industry: 'Global',
    type: 'training',
    title: 'Objection: Authority Block',
    prompt_text:
      "Prospect says: 'I need to run this by my boss.' Acknowledge, then ask: 'What questions will they have? Let's make sure you have everything to present confidently.'",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'objection', 'authority'],
    hints: [
      "Acknowledge without resistance: 'That makes total sense. It's smart to involve them.' No pushback—that builds resentment.",
      "Ask about the gatekeeper: 'Who will they be presenting to? What's important to them?' Understand the next person's priorities.",
      "Offer support: 'Would it help if I drafted a summary of what we discussed? Or joined a follow-up call to answer their questions?'",
      "Empower them to present: 'What's the most important takeaway from our conversation? What should you lead with?' Help them feel confident.",
      "Create urgency gently: 'When do you think you'll get on their calendar?' keeps momentum without pressure. 'I'll follow up Thursday to see where you stand.'"
    ]
  },

  // === CLOSING & NEXT STEPS ===
  {
    id: 'curated_beg_024',
    industry: 'Global',
    type: 'training',
    title: 'Set Next Step with Confidence',
    prompt_text:
      "Close the conversation by confirming alignment and proposing a clear, time-bound next step. Use assumptive language: 'Based on what we discussed, let's...' Keep it simple and universal.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'close', 'confidence'],
    hints: [
      "Summarize alignment first: 'Based on what we discussed, it sounds like X is important to you, and Y would help.' Confirms shared understanding.",
      "Assume forward movement: 'Here's how I see us moving forward...' not 'If you want to move forward...' Presumes next steps.",
      "Be specific about timing: Not 'Let's stay in touch' but 'Let's reconnect Thursday at 2 PM to discuss the proposal.' Concrete beats vague.",
      "Propose, don't ask: 'Here's the timeline I suggest...' is stronger than 'Would you be open to...' But stay open to adjustment.",
      "Get confirmation: 'Does Thursday at 2 work for you?' Make sure they're committed, not just polite. Repeat the time to confirm."
    ]
  },
  {
    id: 'curated_beg_025',
    industry: 'Global',
    type: 'training',
    title: 'Trial Close: Test Engagement',
    prompt_text:
      "Practice a trial close mid-conversation: 'How are you feeling about what we've discussed so far?' Read their response to gauge interest and uncover hidden objections.",
    difficulty_level: 1,
    tags: ['universal', 'beginner_core', 'training_core', 'close', 'qualification'],
    hints: [
      "Try a soft trial close: 'How are you feeling about this so far?' or 'Does this direction make sense?' Gets temperature check without heavy ask.",
      "Listen to their tone and pace: Enthusiasm? Hesitation? Flatness? Emotion tells you more than words. Adjust your approach based on tone.",
      "Probe for objections: If they seem lukewarm, ask: 'What questions do you have?' or 'Is there anything that doesn't fit?' Surface issues early.",
      "Use trial closes multiple times: Not just at the end. Mid-call trial closes let you recalibrate without wasting time on wrong direction.",
      "Don't take objections as rejections: 'I'm concerned about cost' is not 'no.' It's 'I need to understand value better.' Address the underlying concern."
    ]
  }
];

/**
 * Service for managing drills in the Supabase database
 * Handles CRUD operations with proper case conversion between snake_case (DB) and camelCase (React)
 * Implements personality-based and performance-based difficulty filtering
 */
export const drillsService = {
  /**
   * Get all drills with adaptive difficulty filtering based on performance
   * @param {Object} filters - Optional filters (industry, type, difficultyLevel, skillLevel, userId, adaptive)
   * @returns {Promise<Array>} Array of drills in camelCase
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('drills')?.select('*')?.order('created_at', { ascending: false });

      query = applyTenant(query, filters?.tenantId);

      // Apply filters if provided
      if (filters?.industry) {
        query = query?.eq('industry', filters?.industry);
      }
      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      
      // Use adaptive difficulty if userId provided and adaptive flag is true
      if (filters?.adaptive && filters?.userId) {
        try {
          const dynamicDifficulty = await adaptiveLearningService.getDynamicDifficulty(
            filters.userId,
            'drill'
          );
          query = query
            ?.gte('difficulty_level', dynamicDifficulty.minDifficulty)
            ?.lte('difficulty_level', dynamicDifficulty.maxDifficulty);
        } catch (err) {
          console.warn('Adaptive difficulty failed, falling back to skill level:', err);
          // Fall through to skill level filtering
        }
      }
      
      // Apply skill-level based difficulty filtering if skillLevel provided (fallback)
      if (filters?.skillLevel && !filters?.adaptive) {
        const difficultyConfig = getDifficultyConfig(filters.skillLevel);
        const [minDiff, maxDiff] = difficultyConfig.difficultyRange;
        query = query?.gte('difficulty_level', minDiff)?.lte('difficulty_level', maxDiff);
      } else if (filters?.difficultyLevel) {
        // Fallback to direct difficulty filter if no skillLevel
        query = query?.eq('difficulty_level', filters?.difficultyLevel);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Apply client-side tag filtering for beginner quality if requested
      let filtered = data || [];
      if (filters?.beginnerQuality) {
        const includeTags = new Set(['universal', 'beginner_core', 'training_core', 'training']);
        const excludeTags = new Set(['enterprise', 'multi_stakeholder', 'niche', 'industry_specific']);
        
        filtered = filtered.filter(drill => {
          const tags = Array.isArray(drill?.tags) ? drill.tags : [];
          const tagStrs = tags.map(t => String(t || '').toLowerCase());
          
          if (filters.beginnerQuality === 'universal') {
            const hasUniversal = tagStrs.some(t => includeTags.has(t));
            const hasNiche = tagStrs.some(t => excludeTags.has(t));
            return hasUniversal && !hasNiche;
          } else if (filters.beginnerQuality === 'niche') {
            return tagStrs.some(t => excludeTags.has(t));
          }
          return true;
        });
      }

      // Convert snake_case to camelCase
      return filtered?.map(drill => ({
        id: drill?.id,
        industry: drill?.industry,
        type: drill?.type,
        title: drill?.title,
        promptText: drill?.prompt_text,
        difficultyLevel: drill?.difficulty_level,
        tags: drill?.tags,
        createdAt: drill?.created_at,
        updatedAt: drill?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching drills:', error);
      throw error;
    }
  },

  /**
   * Get today's drill - consistent across all pages with skill-level filtering
   * Uses date-based selection to ensure same drill all day
   * @param {Object} filters - Optional filters (industry, type, difficultyLevel, skillLevel)
   * @returns {Promise<Object>} Today's drill in camelCase
   */
  async getTodaysDrill(filters = {}) {
    try {
      let query = supabase?.from('drills')?.select('*')?.order('created_at', { ascending: false });

      query = applyTenant(query, filters?.tenantId);

      // Apply filters if provided
      if (filters?.industry) {
        query = query?.eq('industry', filters?.industry);
      }
      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      
      // Apply skill-level based difficulty filtering if skillLevel provided
      if (filters?.skillLevel) {
        const difficultyConfig = getDifficultyConfig(filters.skillLevel);
        const [minDiff, maxDiff] = difficultyConfig.difficultyRange;
        query = query?.gte('difficulty_level', minDiff)?.lte('difficulty_level', maxDiff);
      } else if (filters?.difficultyLevel) {
        query = query?.eq('difficulty_level', filters?.difficultyLevel);
      }

      const { data, error } = await query;

      if (error) throw error;
      if (!data || data?.length === 0) return null;

      // Use date-based selection for consistency
      const today = new Date();
      const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
      const index = dayOfYear % data.length;
      const drill = data[index];

      // Convert snake_case to camelCase
      return {
        id: drill?.id,
        industry: drill?.industry,
        type: drill?.type,
        title: drill?.title,
        promptText: drill?.prompt_text,
        difficultyLevel: drill?.difficulty_level,
        tags: drill?.tags,
        createdAt: drill?.created_at,
        updatedAt: drill?.updated_at
      };
    } catch (error) {
      console.error('Error fetching today\'s drill:', error);
      throw error;
    }
  },

  /**
   * Get today's drill based on user level with spaced repetition
   * Selects drills matching user's current level, plus some weaker drills they've completed
   * @param {number} userLevel - Current user level (1-5)
   * @param {Object} userProfile - User profile data
   * @returns {Promise<Object>} Today's drill in camelCase
   */
  async getTodaysDrillByLevel(userLevel = 1, userProfile = {}, tenantId = null) {
    try {
      // Determine difficulty policy
      // Beginner should ONLY see difficulty 1 and universal drills
      const skillLevel = userProfile?.skillLevel || userProfile?.skill_level || 'beginner';
      const isBeginner = (userLevel <= 1) || (String(skillLevel).toLowerCase() === 'beginner');
      const minDifficulty = isBeginner ? 1 : Math.max(1, userLevel - 1);
      const maxDifficulty = isBeginner ? 1 : Math.min(5, userLevel + 1);

      console.log('Fetching drills for user level:', userLevel, 'difficulty range:', minDifficulty, '-', maxDifficulty);
      console.log('User profile:', userProfile);
      console.log('User industry filter:', userProfile?.industry);

      // Fetch drills
      let query = supabase
        ?.from('drills')
        ?.select('*')
        ?.gte('difficulty_level', minDifficulty)
        ?.lte('difficulty_level', maxDifficulty)
        ?.order('id', { ascending: true });

      query = applyTenant(query, tenantId);

      // Industry policy:
      // - For beginners: force Global-only to keep scenarios universal
      // - For others: Global + user industry
      if (isBeginner) {
        query = query?.eq('industry', 'Global');
      } else if (userProfile?.industry && userProfile.industry.trim() !== '') {
        query = query?.or(`industry.eq.Global,industry.eq.${userProfile.industry}`);
      } else {
        query = query?.eq('industry', 'Global');
      }

      const { data: levelDrills, error } = await query;

      if (error) {
        console.error('Error fetching drills from database:', error);
        throw error;
      }
      
      console.log('Drills fetched from database:', levelDrills?.length || 0, 'drills');
      
      if (!levelDrills || levelDrills?.length === 0) {
        console.warn('No drills found in database for difficulty range:', minDifficulty, '-', maxDifficulty);
      }

      // Apply tag filtering in JS for universal beginner quality
      const excludeTags = new Set(['enterprise', 'multi_stakeholder', 'niche', 'industry_specific']);
      const includeTags = new Set(['universal', 'beginner_core', 'training_core']);

      let filtered = (levelDrills || []).filter(d => {
        const tags = Array.isArray(d?.tags) ? d.tags : [];
        if (isBeginner) {
          // Must include at least one include tag
          const includesCore = tags.some(t => includeTags.has(String(t).toLowerCase()));
          const hasExcluded = tags.some(t => excludeTags.has(String(t).toLowerCase()));
          return includesCore && !hasExcluded;
        }
        return true;
      });

      // Get user's completed drills (weaker ones for spaced repetition)
      // Note: Using 'score' column instead of 'lumo_score'
      let userDrills = null;
      let userDrillsError = null;
      if (userProfile?.id) {
        const res = await supabase
          ?.from('user_drills')
          ?.select('drill_id, score')
          ?.eq('user_id', userProfile?.id)
          ?.order('score', { ascending: true })
          ?.limit(5);
        userDrills = res?.data;
        userDrillsError = res?.error;
      }

      if (userDrillsError && userDrillsError?.code !== 'PGRST116') {
        console.warn('Could not fetch user drill history:', userDrillsError);
      }

      // Create pool: current level drills + spaced repetition of weaker drills
      let drillPool = [...filtered];
      
      if (userDrills && userDrills?.length > 0) {
        // Fetch the weak drills to add them to the pool
        const weakDrillIds = userDrills.map(ud => ud?.drill_id);
        
        if (weakDrillIds?.length > 0) {
          const { data: weakDrills } = await applyTenant(
            supabase
              ?.from('drills')
              ?.select('*')
              ?.in('id', weakDrillIds),
            tenantId
          );

          if (weakDrills) {
            // Add weaker drills with 30% probability (spaced repetition)
            drillPool = [...filtered, ...weakDrills];
          }
        }
      }

      // If beginner and pool is empty, use curated universal beginner drills as fallback
      if (isBeginner && drillPool.length === 0) {
        drillPool = [...CURATED_BEGINNER_DRILLS];
      }

      // Use date-based selection for consistency (same drill all day)
      const today = new Date();
      const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
      const index = dayOfYear % drillPool.length;

      const drill = drillPool[index];

      // Convert snake_case to camelCase
      return {
        id: drill?.id,
        industry: drill?.industry,
        type: drill?.type,
        title: drill?.title,
        promptText: drill?.prompt_text,
        difficultyLevel: drill?.difficulty_level,
        tags: drill?.tags,
        hints: drill?.hints || [],
        createdAt: drill?.created_at,
        updatedAt: drill?.updated_at
      };
    } catch (error) {
      console.error('Error fetching today\'s drill by level:', error);
      throw error;
    }
  },

  /**
   * Get random drills by user level (for drill selection, not daily)
   * @param {number} userLevel - Current user level (1-5)
   * @param {Object} userProfile - User profile data
   * @param {number} count - Number of drills to return
   * @returns {Promise<Array>} Array of random drills matching user level
   */
  async getRandomDrillsByLevel(userLevel = 1, userProfile = {}, count = 1) {
    try {
      // Map user level to difficulty ranges
      const skillLevel = userProfile?.skillLevel || userProfile?.skill_level || 'beginner';
      const isBeginner = (userLevel <= 1) || (String(skillLevel).toLowerCase() === 'beginner');
      const minDifficulty = isBeginner ? 1 : Math.max(1, userLevel - 1);
      const maxDifficulty = isBeginner ? 1 : Math.min(5, userLevel + 1);

      let query = supabase
        ?.from('drills')
        ?.select('*')
        ?.gte('difficulty_level', minDifficulty)
        ?.lte('difficulty_level', maxDifficulty);

      // For beginners, force Global-only universal drills
      if (isBeginner) {
        query = query?.eq('industry', 'Global');
      } else if (userProfile?.industry) {
        query = query?.or(`industry.eq.Global,industry.eq.${userProfile?.industry}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      if (!data || data?.length === 0) {
        return isBeginner ? [...CURATED_BEGINNER_DRILLS].slice(0, count).map(d => ({
          id: d.id,
          industry: d.industry,
          type: d.type,
          title: d.title,
          promptText: d.prompt_text,
          difficultyLevel: d.difficulty_level,
          tags: d.tags,
          createdAt: null,
          updatedAt: null
        })) : [];
      }

      // Apply tag filtering for universal beginner drills
      const excludeTags = new Set(['enterprise', 'multi_stakeholder', 'niche', 'industry_specific']);
      const includeTags = new Set(['universal', 'beginner_core', 'training_core']);
      const filtered = (data || []).filter(d => {
        const tags = Array.isArray(d?.tags) ? d.tags : [];
        if (isBeginner) {
          const includesCore = tags.some(t => includeTags.has(String(t).toLowerCase()));
          const hasExcluded = tags.some(t => excludeTags.has(String(t).toLowerCase()));
          return includesCore && !hasExcluded;
        }
        return true;
      });

      // Shuffle and return requested count
      const pool = filtered.length > 0 ? filtered : (isBeginner ? CURATED_BEGINNER_DRILLS : []);
      const shuffled = pool.sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count).map(drill => ({
        id: drill?.id,
        industry: drill?.industry,
        type: drill?.type,
        title: drill?.title,
        promptText: drill?.prompt_text,
        difficultyLevel: drill?.difficulty_level,
        tags: drill?.tags,
        createdAt: drill?.created_at ?? null,
        updatedAt: drill?.updated_at ?? null
      }));
    } catch (error) {
      console.error('Error fetching random drills by level:', error);
      throw error;
    }
  },

  /**
   * Get a single drill by ID
   * @param {string} id - Drill ID
   * @returns {Promise<Object>} Drill in camelCase
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('drills')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        industry: data?.industry,
        type: data?.type,
        title: data?.title,
        promptText: data?.prompt_text,
        difficultyLevel: data?.difficulty_level,
        tags: data?.tags,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching drill:', error);
      throw error;
    }
  },

  /**
   * Create a new drill (admin only)
   * @param {Object} drill - Drill data in camelCase
   * @returns {Promise<Object>} Created drill in camelCase
   */
  async create(drill) {
    try {
      // Verify user is authenticated and is admin
      const { data, error: authError } = await supabase?.auth?.getUser();
      const user = data?.user;
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const { data: createdDrill, error } = await supabase?.from('drills')?.insert({
          industry: drill?.industry,
          type: drill?.type,
          title: drill?.title,
          prompt_text: drill?.promptText,
          difficulty_level: drill?.difficultyLevel,
          tags: drill?.tags
        })?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: createdDrill?.id,
        industry: createdDrill?.industry,
        type: createdDrill?.type,
        title: createdDrill?.title,
        promptText: createdDrill?.prompt_text,
        difficultyLevel: createdDrill?.difficulty_level,
        tags: createdDrill?.tags,
        createdAt: createdDrill?.created_at,
        updatedAt: createdDrill?.updated_at
      };
    } catch (error) {
      console.error('Error creating drill:', error);
      throw error;
    }
  },

  /**
   * Update an existing drill (admin only)
   * @param {string} id - Drill UUID
   * @param {Object} updates - Drill updates in camelCase
   * @returns {Promise<Object>} Updated drill in camelCase
   */
  async update(id, updates) {
    try {
      // Verify user is authenticated and is admin
      const { data: authData, error: authError } = await supabase?.auth?.getUser();
      const user = authData?.user;
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.industry !== undefined) dbUpdates.industry = updates?.industry;
      if (updates?.type !== undefined) dbUpdates.type = updates?.type;
      if (updates?.title !== undefined) dbUpdates.title = updates?.title;
      if (updates?.promptText !== undefined) dbUpdates.prompt_text = updates?.promptText;
      if (updates?.difficultyLevel !== undefined) dbUpdates.difficulty_level = updates?.difficultyLevel;
      if (updates?.tags !== undefined) dbUpdates.tags = updates?.tags;

      const { data, error } = await supabase?.from('drills')?.update(dbUpdates)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        industry: data?.industry,
        type: data?.type,
        title: data?.title,
        promptText: data?.prompt_text,
        difficultyLevel: data?.difficulty_level,
        tags: data?.tags,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating drill:', error);
      throw error;
    }
  },

  /**
   * Delete a drill (admin only)
   * @param {string} id - Drill UUID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      // Verify user is authenticated and is admin
      const { data: authData } = await supabase?.auth?.getUser();
      const user = authData?.user;
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('drills')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting drill:', error);
      throw error;
    }
  },

  /**
   * Get unique industries from drills
   * @returns {Promise<Array>} Array of unique industry strings
   */
  async getIndustries() {
    try {
      const { data, error } = await supabase?.from('drills')?.select('industry');

      if (error) throw error;

      // Get unique industries
      const industries = [...new Set(data.map(d => d.industry))];
      return industries?.sort();
    } catch (error) {
      console.error('Error fetching industries:', error);
      throw error;
    }
  },

  /**
   * Get unique types from drills
   * @returns {Promise<Array>} Array of unique type strings
   */
  async getTypes() {
    try {
      const { data, error } = await supabase?.from('drills')?.select('type');

      if (error) throw error;

      // Get unique types
      const types = [...new Set(data.map(d => d.type))];
      return types?.sort();
    } catch (error) {
      console.error('Error fetching types:', error);
      throw error;
    }
  },

  /**
   * Submit a user's drill response and evaluation
   * @param {string} drillId - Drill UUID
   * @param {Object} drillData - Drill submission data
   * @returns {Promise<Object>} Created user drill record
   */
  async submitDrillResponse(drillId, drillData) {
    try {
      const { data: authData } = await supabase?.auth?.getUser();
      const user = authData?.user;
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const { data, error } = await supabase
        ?.from('user_drills')
        ?.insert({
          user_id: user?.id,
          drill_id: drillId,
          response_text: drillData?.responseText,
          score: drillData?.score,
          feedback: drillData?.feedback,
          time_taken_seconds: drillData?.timeTakenSeconds,
          completed_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        drillId: data?.drill_id,
        responseText: data?.response_text,
        score: data?.score,
        feedback: data?.feedback,
        timeTakenSeconds: data?.time_taken_seconds,
        completedAt: data?.completed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error submitting drill response:', error);
      throw error;
    }
  },

  /**
   * Get user's drill history
   * @param {number} limit - Number of recent drills to fetch
   * @returns {Promise<Array>} Array of user drill records
   */
  async getUserDrillHistory(limit = 10) {
    try {
      const { data: authData } = await supabase?.auth?.getUser();
      const user = authData?.user;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_drills')
        ?.select(`
          *,
          drills (
            id,
            title,
            industry,
            type,
            difficulty_level
          )
        `)
        ?.eq('user_id', user?.id)
        ?.order('completed_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      // Convert to camelCase
      return data?.map(record => ({
        id: record?.id,
        userId: record?.user_id,
        drillId: record?.drill_id,
        responseText: record?.response_text,
        score: record?.score,
        feedback: record?.feedback,
        timeTakenSeconds: record?.time_taken_seconds,
        completedAt: record?.completed_at,
        createdAt: record?.created_at,
        updatedAt: record?.updated_at,
        drill: {
          id: record?.drills?.id,
          title: record?.drills?.title,
          industry: record?.drills?.industry,
          type: record?.drills?.type,
          difficultyLevel: record?.drills?.difficulty_level
        }
      }));
    } catch (error) {
      console.error('Error fetching user drill history:', error);
      throw error;
    }
  }
};
